import { memo, useCallback } from 'react';
import { Position, useEdges } from 'reactflow';

import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useInputSizeTracking } from '../../hooks/useInputSizeTracking';
import useFlowsStore from '../../store/storeFlows';
import { NodeProps } from '../../util/Types/NodeProps';
import { useNodeFieldAutoSave } from '../../util/autosave/useNodeFieldAutoSave';
import { arrayDataToText } from '../../util/dataToText';
import { isActiveEdge } from '../../util/findActiveHandle';
import TagInput from '../Test/TagInput';
import InputHandle from '../UI/InputHandle/InputHandle';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import { WordCount } from './AiTextGenerator';

const DEFAULT_INPUT_WIDTH = '360px';
const DEFAULT_INPUT_HEIGHT = '96px';

function TextBoxNode({ id, data, type }: NodeProps) {
  const { setNodeState, setSaving, setSchema } = useFlowsStore(state => state);

  const { flowId } = useParams();
  const edges = useEdges();

  const inputSize = useInputSizeTracking({
    nodeId: id,
    inputSize: data?.inputSize,
    collapsed: data?.collapsed,
    edges,
    flowId,
    setNodeState,
    setSaving,
    setSchema,
  });

  const connectionsText =
    Array.isArray(data?.textData) && data.textData.length > 0
      ? data.textData
          .map((item: { text: string }) => item.text)
          .join('\n')
      : undefined;
  const externalValue = connectionsText ?? data?.text ?? '';

  const [textBoxValue, setTextBoxValue] = useNodeFieldAutoSave<string>({
    externalValue,
    fieldName: 'text',
    nodeId: id,
    flowId,
    edges,
    setNodeState,
    setSaving,
    setSchema,
    changeType: 'changeText',
    debounceMs: 500,
  });

  const onChange = useCallback(
    (evt: { target: { value: string } }) => setTextBoxValue(evt.target.value),
    [setTextBoxValue],
  );

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  return (
    <Node
      id={id}
      data={data}
      isLoading={false}
      type={type}
      showTokensUsed={false}
    >
      <Box position={'relative'} p={'16px'}>
        {!data.collapsed && (
          <Box>
            <TagInput
              name={'text'}
              disabled={data?.disabled}
              value={
                data?.['textData']?.length > 0
                  ? arrayDataToText(data?.['textData'])
                  : textBoxValue
              }
              findColor={findColor}
              isActive={isActiveEdge(edges, id, 'inputText', 'target')}
              labelName={'Text'}
              isCollapsed={true}
              readOnly={data?.['textData']?.length > 0}
              handleId={'inputText'}
              nodeLabel={data?.label}
              nodeId={id}
              onChange={onChange}
              width={inputSize?.width || DEFAULT_INPUT_WIDTH}
              height={inputSize?.height || DEFAULT_INPUT_HEIGHT}
            />

            <WordCount
              text={
                data?.['textData']?.length > 0
                  ? arrayDataToText(data?.['textData'])
                  : textBoxValue
              }
            />
            <InputHandle
              activeColor={findColor?.color}
              handleId={'textOutput'}
              isActive={isActiveEdge(edges, id, 'textOutput', 'source')}
              right={-10}
              top={'50%'}
              position={Position.Right}
              type="source"
            />
          </Box>
        )}
      </Box>
    </Node>
  );
}

export default memo(TextBoxNode);
